/**
 * 自由创作「素材」行(0924 车 B,老板拍板:视频不放开、图片放开)—— 纯函数单点。
 *
 * 三选一:
 *   - `none`  不用素材(0914 以来的直发形态,payload 逐字不变);
 *   - `frame` 上传首帧图(可再传尾帧)→ 画面**从这张图开始**(I2V / I2V_FIRST_LAST);
 *   - `refs`  参考图 ≤4 张 → **保持这件东西的样子,画面自由**(R2V,references)。
 *
 * ★互斥是硬约束不是 UI 偏好:首帧/尾帧与 reference_image 同发,上游直接 400(契约 §3.3;
 *   api 422 `FRAME_AND_REFERENCE_EXCLUSIVE`、ai 也有闸)。所以切模式时**另一侧整块清空**,
 *   不是藏起来 —— 藏起来的值照样会被组包带出去。
 *
 * ★payload 形态以《视频仓0924-通用规矩与契约》§3.3 为准,改之前先改契约:
 *   首帧: generationMode I2V(由 inferMode 按 firstFrameUrl 推)+ shotMode ["I2V"]
 *         + firstFrameUrl + panelFirstFrameKeys [key] + ratio "adaptive"(画幅跟图走);
 *   尾帧: generationMode I2V_FIRST_LAST + lastFrameUrl + lastFrameKey;
 *   参考: references [[{assetRef:"key://<objectKey>", role:"product"}]](单镜 → 外层一项);
 *         job 文案是 ai 侧常量,console 不写;scene 角色契约留着、本批 UI 不露。
 *
 * ★报价:自传首帧不出图,不加 40 分出帧费 —— 自由流报价只问 `video.render × 直发秒数`
 *   (VideoCreatePage `quotedBareSend`,不含台本图那笔),本模块不碰报价。
 */
import type {
  GenerationMode,
  VideoShotReference,
  VideoTaskCreateRequest,
} from "../api/videoService";
import type { FormState, FreeMaterialMode, FreeRefImage } from "./videoCreateTypes";

/** 参考图上限(老板拍板 ≤4;与 ai reference_compiler product 配额 4 同数)。 */
export const FREE_REF_MAX = 4;

/** 参考图 assetRef 的新形态前缀(ai `_resolve_url` 按对象前缀选桶、api 过租户闸)。 */
export const KEY_REF_PREFIX = "key://";

/** 每种模式一句话讲清楚它做什么(老板要求:首帧 vs 参考的区别要说清)。 */
export const FREE_MATERIAL_HINT: Record<FreeMaterialMode, string> = {
  none: "只用文字出画面。",
  frame: "首帧 = 画面从这张图开始动起来,画幅跟图走;可再传一张尾帧,画面停在那张图上。",
  refs: "参考 = 保持这件东西的样子(产品/物件/场景),画面怎么拍由文字决定。",
};

export const FREE_MATERIAL_PERSON_NOTE = "人物照请去人物库建档,这里传产品/场景图。";

export function freeMaterialModeOf(form: FormState): FreeMaterialMode {
  return form.freeMaterialMode ?? "none";
}

/** 首帧那一侧整块清空(首帧 + 尾帧的 url 与 key)。 */
function clearFrames(f: FormState): FormState {
  return {
    ...f,
    firstFrameUrl: undefined,
    firstFrameKey: null,
    lastFrameUrl: undefined,
    lastFrameKey: null,
  };
}

/**
 * 切换素材模式 —— **切换即清另一侧**(互斥)。
 * 切到 none 两侧都清;同模式重复点不动(不把已传的图误清掉)。
 */
export function switchFreeMaterialMode(f: FormState, mode: FreeMaterialMode): FormState {
  if (freeMaterialModeOf(f) === mode) return f;
  const base = { ...f, freeMaterialMode: mode };
  if (mode === "frame") return { ...base, freeRefImages: null };
  if (mode === "refs") return clearFrames(base);
  return { ...clearFrames(base), freeRefImages: null };
}

export function withFirstFrame(f: FormState, img: FreeRefImage): FormState {
  return {
    ...f,
    freeMaterialMode: "frame",
    freeRefImages: null,
    firstFrameUrl: img.url,
    firstFrameKey: img.key,
  };
}

/** 去掉首帧 = 尾帧也一起去(只有尾帧没有首帧不成立)。 */
export function withoutFirstFrame(f: FormState): FormState {
  return clearFrames(f);
}

export function withLastFrame(f: FormState, img: FreeRefImage | null): FormState {
  return img
    ? { ...f, lastFrameUrl: img.url, lastFrameKey: img.key }
    : { ...f, lastFrameUrl: undefined, lastFrameKey: null };
}

/** 追加参考图(按 key 去重,超过上限的丢弃)。 */
export function addFreeRefImages(f: FormState, imgs: FreeRefImage[]): FormState {
  const cur = f.freeRefImages ?? [];
  const next = [...cur];
  for (const img of imgs) {
    if (next.length >= FREE_REF_MAX) break;
    if (!next.some((x) => x.key === img.key)) next.push(img);
  }
  return { ...clearFrames(f), freeMaterialMode: "refs", freeRefImages: next };
}

export function removeFreeRefImage(f: FormState, key: string): FormState {
  return { ...f, freeRefImages: (f.freeRefImages ?? []).filter((x) => x.key !== key) };
}

/** 参考图 → 单镜 references(外层一项;role 本批恒 product)。 */
export function freeRefReferences(imgs: FreeRefImage[]): VideoShotReference[] {
  return imgs.map((img) => ({ assetRef: `${KEY_REF_PREFIX}${img.key}`, role: "product" }));
}

/**
 * 素材行 → 请求体补丁(buildTaskRequest 末尾覆盖进去)。
 *
 * - none / 没传图 → `{}`(与 0914 以来的直发 payload 逐字相同);
 * - 多镜(panelScenes ≥2)→ `{}`:素材行只属于自由创作单镜直发,多镜有自己的每镜首帧;
 * - frame → 见头注;没有尾帧时显式抹掉 lastFrameUrl,防旧值漏发。
 * - refs  → references 单镜一项;显式抹掉首/尾帧(双保险,互斥在切换时已清)。
 */
export function freeMaterialRequest(
  form: FormState,
  baseReferences?: VideoShotReference[][]
): Partial<VideoTaskCreateRequest> {
  if (form.panelScenes && form.panelScenes.length >= 2) return {};
  const mode = freeMaterialModeOf(form);
  if (mode === "frame" && form.firstFrameKey && form.firstFrameUrl) {
    const hasLast = !!(form.lastFrameKey && form.lastFrameUrl);
    return {
      firstFrameUrl: form.firstFrameUrl,
      panelFirstFrameKeys: [form.firstFrameKey],
      shotMode: ["I2V"],
      ratio: "adaptive",
      lastFrameUrl: hasLast ? form.lastFrameUrl : undefined,
      lastFrameKey: hasLast ? form.lastFrameKey! : undefined,
    };
  }
  const refs = form.freeRefImages ?? [];
  if (mode === "refs" && refs.length > 0) {
    // 单镜:若已有(身份等)参考派活,同一镜里并排;自由流现无选人入口,通常为空。
    const existing = baseReferences && baseReferences.length <= 1 ? (baseReferences[0] ?? []) : [];
    return {
      firstFrameUrl: undefined,
      lastFrameUrl: undefined,
      references: [[...existing, ...freeRefReferences(refs.slice(0, FREE_REF_MAX))]],
    };
  }
  return {};
}

/**
 * 提交前闸(自由创作):选了素材模式却没传图 / 参考图配了不认参考的引擎 → 人话拦下。
 * 返回 null = 放行。
 */
export function freeMaterialBlockReason(
  form: FormState,
  effectiveMode: GenerationMode,
  provider: string
): string | null {
  const mode = freeMaterialModeOf(form);
  if (mode === "frame") {
    if (!form.firstFrameKey) return "选了「上传首帧图」但还没传图 —— 传一张,或切回「不用素材」";
    if (effectiveMode === "T2V")
      return "已上传首帧图,但当前档位只支持文生视频,会忽略首帧 —— 请在「高级」里换能图生视频的档位(如「专业·综合」)";
  }
  if (mode === "refs") {
    if ((form.freeRefImages ?? []).length === 0)
      return "选了「参考图」但还没传图 —— 传 1~4 张产品/场景图,或切回「不用素材」";
    if (provider !== "seedance")
      return "参考图只有「综合」档支持 —— 请在「高级」里换「综合」档,或打开智能选型";
  }
  return null;
}
